<template>
  <div class="navbar-mobile-side  bg-white border-b-gray-800 border-b-[1px] flex xl:hidden flex-col overflow-hidden"
    :class="{ opened: isOpen }">
    <div class="flex flex-row justify-between items-center max-w-screen h-full max-h-[66px]">
      <button class="nav-item call-back ml-4" @click="setCallBack(true)">
        {{ t("header.call_back") }}
      </button>
      <button class="navbar-toggler relative !w-20 !h-20 ml-auto my-auto mr-4 transition-all duration-300" type="button"
        aria-label="Toggle navigation" @click="navMenu">
        <img :src="isOpen ? navCloseIcon : navOpenIcon" :alt="isOpen ? 'Close Menu' : 'Open Menu'"
          class="w-full h-full object-contain transition-transform duration-300" :class="{ 'rotate-180': isOpen }" />
      </button>
    </div>
    <div v-if="isOpen">
      <div class="menu">
        <ul>
          <li class="nav-item">
            <nuxt-link :class="{ active: isMain }" :to="localePath({ path: '/' })">{{ t("header.main") }}</nuxt-link>
          </li>
          <li class="nav-item">
            <nuxt-link :class="{ active: isContacts }" :to="localePath({ path: '/contacts' })">{{ t("header.contacts")
              }}</nuxt-link>
          </li>
          <li class="nav-item">
            <nuxt-link :class="{ active: isGallery }" :to="localePath({ path: '/gallery' })">{{ t("header.gallery")
              }}</nuxt-link>
          </li>
          <li class="nav-item">
            <nuxt-link :class="{ active: isAbout }" :to="localePath({ path: '/about' })">{{ t("header.about")
              }}</nuxt-link>
          </li>
          <li class="nav-item">
            <nuxt-link :class="{ active: isRestaurant }" :to="localePath({ path: '/restaurant' })">{{
              t("header.restaurant") }}</nuxt-link>
          </li>
          <li class="nav-item">
            <nuxt-link :class="{ active: isRooms }" :to="localePath({ path: '/rooms' })">{{ t("header.rooms")
              }}</nuxt-link>
          </li>
        </ul>
      </div>
      <div class="bottom">
        <ul>
          <li class="nav-item">
            <ul class="flex flex-row gap-2 justify-start items-center">
              <li class="social">
                <a href="https://www.facebook.com/LeonHotel.abkhazia">
                  <img src="/img/facebook.svg" alt="Facebook" />
                </a>
              </li>
              <li class="social">
                <a href="https://api.whatsapp.com/send?phone=79409621100">
                  <img src="/img/whatsapp.svg" alt="WhatsApp" />
                </a>
              </li>
              <li class="social">
                <a href="https://www.instagram.com/hotel_restaurant_leon">
                  <img src="/img/instagram.svg" alt="Instagram" />
                </a>
              </li>
            </ul>
          </li>
          <li class="nav-item lang">
            <ul class="flex flex-row justify-end">
              <li>
                <nuxt-link class="text-black font-lato text-xl" :class="{ 'text-[#d9ccaf]': $i18n.locale === 'ru' }"
                  :to="switchLocalePath('ru')">
                  {{ t("links.ru") }}
                </nuxt-link>
              </li>
              <li class="mx-2 text-black font-lato text-xl">/</li>
              <li>
                <nuxt-link class="text-black font-lato text-xl" :class="{ 'text-[#d9ccaf]': $i18n.locale === 'en' }"
                  :to="switchLocalePath('en')">
                  {{ t("links.en") }}
                </nuxt-link>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import navCloseIcon from '~/assets/svgs/close-popup.svg'
import navOpenIcon from '~/assets/svgs/nav.svg'

const store = useMainStore()
const route = useRoute()
const { t, setLocale } = useI18n()
const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()

// Props
defineProps<{
  isMain: boolean
  isContacts: boolean
  isGallery: boolean
  isAbout: boolean
  isRestaurant: boolean
  isRooms: boolean
}>()

// Emits
defineEmits<{
  (e: 'order-room'): void
}>()

// Computed
const isOpen = ref(false)

// Methods
const setCallBack = (value: boolean) => store.setCallBack(value)
const navMenu = () => isOpen.value = !isOpen.value

// Watch for route changes to close menu
watch(route, () => {
  if (isOpen.value) {
    isOpen.value = false
  }
})

// Watch for menu state to manage body overflow
watch(isOpen, (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

// Cleanup on component unmount
onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<style scoped lang="scss">
@media (max-width: 1200px) {
  .navbar-mobile-side {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 66px;
    transition: all 0.3s ease;
    z-index: 15;

    &.opened {
      height: 100vh;
      background-color: white;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    .top {
      display: flex;
      height: 66px;
      width: 100%;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      position: fixed;
      top: 0;
      left: 0;
      background-color: transparent;
      z-index: 2;
    }

    &.opened .top {
      background-color: white;
    }

    .request_call {
      text-align: left;
      text-decoration: underline;
      font-size: 19px;
      margin: 22px 0 0 22px;
      color: #ffffff;
      font-family: "Lato";
      cursor: pointer;
    }

    &.opened .request_call {
      color: #000000;
    }

    .menu {
      padding-top: 86px;
      height: calc(100vh - 156px);
      overflow-y: auto;

      ul {
        display: flex;
        flex-direction: column;
        padding: 20px 70px;
        list-style: none;
        margin: 0;

        li {
          margin-bottom: 25px;

          a {
            color: #000;
            font-size: 23px;
            text-decoration: none;
            font-family: "Lato";

            &.active {
              color: #d9ccaf;
            }
          }
        }
      }
    }

    .bottom {
      position: fixed;
      bottom: 90px;
      left: 0;
      width: 100%;
      padding: 0 74px;
      z-index: 2;

      ul {
        width: auto;
        display: flex;
        justify-content: flex-start;
        gap: 20px;
        list-style: none;
        margin: 0;
        padding: 0;
      }

      img {
        filter: none !important;
        width: 29px;
        height: 29px;
      }
    }

    .navbar-toggler {
      background: transparent;
      border: none;
      cursor: pointer;
      outline: none;

      &:hover {
        opacity: 0.8;
      }

      img {
        filter: invert(1);
      }
    }

    &.opened .navbar-toggler img {
      filter: none;
    }

    .navbar-close-icon {
      position: relative;
      width: 45px;
      height: 45px;
      // background: url(/img/close-popup.svg) no-repeat center/contain;
      cursor: pointer;
    }

    .logoImage {
      position: absolute;
      left: 20px;
      top: 9px;
      height: 48px;
      width: 83px;
      background-color: transparent;

      img {
        height: 100%;
        filter: none;
      }
    }

    .mobile_button {
      width: 211px;
      height: 49px;
      position: fixed;
      bottom: 20px;
      margin: auto;
      left: 0;
      right: 0;
      text-align: center;
      background: #d9ccaf;
      z-index: 16;
      border-radius: 0;

      .submit {
        width: 100%;
        height: 100%;
        color: black;
        font-family: "Lato";
        font-size: 16px;
        border: none;
        background: transparent;
        cursor: pointer;
      }
    }
  }
}
</style>
